import React from 'react';
import styled from 'styled-components';
import { Card, Button, Tag, Dropdown, Menu } from 'antd';
import { CloseOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../i18n/useTranslation';

import { ReactComponent as DownloadOutlined } from '../../../images/downloaded-outline.svg';
import { ReactComponent as DeleteOutlined } from '../../../images/delete-outline.svg';
import { ReactComponent as DragIndicator } from '../../../images/drag-indicator.svg';

export interface ActionAbleItems {
    key: string;
    name: string;
    onClick: () => void;
    prefixIcon?: React.FC<any>;
    dropDownActionAbleItems?: ActionAbleItems[];
    suffixIcon?: React.FC<any>;
}

interface Props {
    type?: string;
    hideExport?: boolean;
    selectedItems: string[];
    exportCsv?: () => void;
    confirmDatasetDeletion?: () => void;
    confirmGlossaryTermsDeletion?: () => void;
    clearSelectedItems: () => void;
    onRemove?: () => void;
    actionAbleItems?: ActionAbleItems[];
}

const Flex = styled.div`
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    max-width: 100%;
`;

const DeleteButton = styled(Button)`
    background-color: white !important;
    span {
        color: #d96758 !important;
    }
`;

const ExportCSVButton = styled(Button)`
    background-color: white !important;
    span {
        color: #5f767c !important;
    }
`;

const ActionAbleButton = styled(Button)`
    background-color: white !important;
    span {
        color: #5f767c !important;
    }
`;

const Layout = styled.div`
    display: flex;
    gap: 8px;
`;

const DATASET = 'dataset';
const GLOSSARY_TERM_TAB = 'glossaryTermTab';

export const BulkAction = ({
    selectedItems,
    type,
    exportCsv,
    hideExport,
    confirmDatasetDeletion,
    confirmGlossaryTermsDeletion,
    clearSelectedItems,
    onRemove,
    actionAbleItems,
}: Props) => {
    const isDataset = type === DATASET;
    const isGlossaryTermTab = type === GLOSSARY_TERM_TAB;
    const { t } = useTranslation();

    return selectedItems?.length ? (
        <Card className="f-card f-bulk-action-bar">
            <Layout className="pa-2">
                <Flex>
                    <Flex className="shrink">
                        <DragIndicator height={20} width={20} color="#BCBDBF" />
                    </Flex>
                    <Layout>
                        <Flex className="f-text-big-content f-color-blue-s100 f-text-semibold shrink">
                            <Tag className="grey-tag">
                                <span>{selectedItems.length}</span>
                            </Tag>
                        </Flex>
                        <Flex className="f-text-big-content f-color-dark-black-s100 flex">{t('Selected')}</Flex>
                    </Layout>
                </Flex>
                {actionAbleItems && actionAbleItems.length > 0 ? (
                    <>
                        {actionAbleItems.map((actionAbleItem) => (
                            <Flex key={actionAbleItem.key} className="shrink">
                                {actionAbleItem.dropDownActionAbleItems ? (
                                    <Dropdown
                                        trigger={['click']}
                                        placement="bottomRight"
                                        overlay={
                                            <Menu className="f-dropdown-small">
                                                {actionAbleItem.dropDownActionAbleItems.map((dropDownItem) => (
                                                    <Menu.Item key={dropDownItem.key} onClick={dropDownItem.onClick}>
                                                        {dropDownItem.name}
                                                    </Menu.Item>
                                                ))}
                                            </Menu>
                                        }
                                    >
                                        <ActionAbleButton
                                            onClick={actionAbleItem.onClick}
                                            icon={
                                                actionAbleItem.prefixIcon && (
                                                    <actionAbleItem.prefixIcon style={{ width: 16 }} color="#d96758" />
                                                )
                                            }
                                        >
                                            {actionAbleItem.name}
                                            {actionAbleItem.suffixIcon && <actionAbleItem.suffixIcon />}
                                        </ActionAbleButton>
                                    </Dropdown>
                                ) : (
                                    <ActionAbleButton
                                        onClick={actionAbleItem.onClick}
                                        icon={
                                            actionAbleItem.prefixIcon ? (
                                                <actionAbleItem.prefixIcon style={{ width: 16 }} color="#d96758" />
                                            ) : (
                                                <></>
                                            )
                                        }
                                    >
                                        {actionAbleItem.name}
                                    </ActionAbleButton>
                                )}
                            </Flex>
                        ))}
                    </>
                ) : (
                    <>
                        {!hideExport && (
                            <Flex className="shrink">
                                <ExportCSVButton
                                    onClick={exportCsv}
                                    icon={<DownloadOutlined style={{ width: 16 }} color="#5F767C" />}
                                >
                                    {t('Export')}
                                </ExportCSVButton>
                            </Flex>
                        )}

                        {!isGlossaryTermTab && (
                            <Flex className="shrink">
                                <DeleteButton
                                    danger
                                    onClick={isDataset ? confirmDatasetDeletion : confirmGlossaryTermsDeletion}
                                    icon={<DeleteOutlined color="#d96758" style={{ width: 10 }} />}
                                >
                                    {isDataset ? t('Delete Datasets') : t('Delete')}
                                </DeleteButton>
                            </Flex>
                        )}
                    </>
                )}

                {isGlossaryTermTab && (
                    <Flex className="shrink">
                        <ExportCSVButton
                            onClick={onRemove}
                            icon={<MinusCircleOutlined style={{ width: 10 }} className="mr-1" />}
                        >
                            {t('Remove Relationship')}
                        </ExportCSVButton>
                    </Flex>
                )}

                <Flex className="shrink">
                    <Button
                        type="text"
                        shape="circle"
                        onClick={() => clearSelectedItems()}
                        icon={<CloseOutlined style={{ width: 14 }} />}
                    />
                </Flex>
            </Layout>
        </Card>
    ) : (
        <></>
    );
};
